import DOMPurify from 'dompurify'

/**
 * Sanitize AI/flow-generated slide HTML before it is injected via dangerouslySetInnerHTML. Strips
 * scripts, event handlers, and other XSS vectors while keeping ordinary presentational markup AND
 * inline SVG. This is defense-in-depth: a slide author should never be able to ship executable HTML.
 *
 * SVG is allowed on purpose so generated charts/diagrams render LIVE while the slide streams in.
 * Without the svg profile, DOMPurify's html-only allowlist strips every <svg>, so a chart stayed
 * invisible through the whole stream and only appeared once the bake step froze it into an <img> —
 * a jarring late pop-in. The svg/svgFilters profiles still drop <script>, foreignObject, and event
 * handlers, so this remains XSS-safe.
 *
 * Client-only by design — the caller (the flow-HTML slide branch in ScaledStage) only renders in the
 * browser. On the server there is no DOM for DOMPurify, so we return empty (it never runs during SSR).
 */
// Memoized: every flow-HTML slide re-runs this on EVERY re-render (zoom settle, SSE stream write,
// selection change ...), and DOMPurify does a full DOM parse of the slide each time — on a large deck
// that alone made the post-gesture settle O(slides × parse). Same input ⇒ same output (DOMPurify is
// deterministic for a fixed config), so a string-keyed cache is safe. Insertion-order eviction keeps
// the streaming case bounded (partial HTML strings churn fast while a slide streams in).
const SANITIZE_CACHE_MAX = 500
const sanitizeCache = new Map<string, string>()

export function sanitizeSlideHtml(html: string): string {
  if (typeof window === 'undefined') return ''
  const cached = sanitizeCache.get(html)
  if (cached !== undefined) return cached
  const clean = DOMPurify.sanitize(html, { USE_PROFILES: { html: true, svg: true, svgFilters: true } })
  if (sanitizeCache.size >= SANITIZE_CACHE_MAX) {
    sanitizeCache.delete(sanitizeCache.keys().next().value as string)
  }
  sanitizeCache.set(html, clean)
  return clean
}

// --- Doc-slide RASTER sanitization (FORCE_BODY) ---------------------------------------------------
// Own memo cache, never shared with `sanitizeCache`: the SAME input string produces DIFFERENT output
// under the two configs (this one keeps a leading <style>, the other deletes it), so a shared cache
// would let whichever variant ran first poison the other's result. Same size/eviction reasoning as
// `sanitizeCache` (DOMPurify re-parses the whole string per call; doc strings churn during streaming).
const docRasterCache = new Map<string, string>()

/**
 * Sanitize a baked DOC-SLIDE string for the SVG-foreignObject thumbnail rasterizer — and for that
 * raster path ONLY.
 *
 * WHY `FORCE_BODY` (proven by reproduction in real Chrome against this repo's DOMPurify): a baked doc
 * string STARTS with its `<style>` block. Without FORCE_BODY, DOMPurify parses the input as a full
 * HTML document, the parser hoists that leading `<style>` into `<head>`, and DOMPurify returns the
 * body only — the entire stylesheet is silently deleted (`DOMPurify.removed` stays empty, so nothing
 * even signals it). The far-zoom thumbnail then renders unstyled: inline root styles only, black or
 * white rectangles with top-left text. With FORCE_BODY the input parses in body context, the leading
 * `<style>` stays put, and the identical SVG-foreignObject pipeline renders pixel-perfect. Scripts,
 * event handlers, and the rest of the XSS surface are still stripped — the profiles are exactly
 * {@link sanitizeSlideHtml}'s.
 *
 * NEVER use this variant for light-DOM injection (dangerouslySetInnerHTML / innerHTML into the app
 * document): the preserved `<style>` there is an app-wide style injection — a doc's stylesheet
 * carries global rules (e.g. `* { animation: none !important }`) that would restyle the entire app.
 * The SVG-foreignObject *image* is sandboxed from the page, which is exactly what makes preserving
 * the stylesheet safe on the raster path and nowhere else.
 */
export function sanitizeDocHtmlForRaster(html: string): string {
  if (typeof window === 'undefined') return ''
  const cached = docRasterCache.get(html)
  if (cached !== undefined) return cached
  const clean = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true, svg: true, svgFilters: true },
    FORCE_BODY: true,
  })
  if (docRasterCache.size >= SANITIZE_CACHE_MAX) {
    docRasterCache.delete(docRasterCache.keys().next().value as string)
  }
  docRasterCache.set(html, clean)
  return clean
}

// --- Inline rich-text sanitization (for an editable text element's preserved formatting) ----------
// A baked text element keeps the generated INLINE runs (bold / italic / underline / colored or sized
// spans / gradient text) as a small HTML string in `SlideElement.textHtml`, which the renderer injects
// via dangerouslySetInnerHTML. That string must therefore be locked to a tiny inline-only allowlist:
// no block tags, no scripts/handlers, and `style` pruned to a handful of typographic properties.

/** The tags allowed inside a text element's rich content — inline emphasis plus lists (`ul`/`ol`/`li`) so a
 *  selection can be turned into an inline bullet/numbered list via execCommand and persisted in `textHtml`.
 *  (`strike` is the legacy strikethrough tag some browsers' execCommand still emits; normalized in render the
 *  same as `s`.) Disallowed block wrappers execCommand sometimes nests in an `<li>` (div/p) are dropped by
 *  DOMPurify with their text kept (KEEP_CONTENT), flattening `<li><div>x</div></li>` → `<li>x</li>`. */
const INLINE_ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'sub', 'sup', 'mark', 'span', 'br', 'ul', 'ol', 'li']

/** Tidy execCommand list markup after sanitizing: drop empty `<li>` and then empty `<ul>`/`<ol>` shells, so a
 *  cleared/toggled-off list never persists as an orphan that keeps a plain paragraph "rich" or renders a blank
 *  bullet. No-op (no extra parse) when the string carries no list tags — the overwhelmingly common case. */
function normalizeInlineLists(html: string): string {
  if (!/<(ul|ol|li)\b/i.test(html)) return html
  const root = document.createElement('div')
  root.innerHTML = html
  root.querySelectorAll('li').forEach((li) => {
    if (!li.textContent || li.textContent.trim() === '') li.remove()
  })
  root.querySelectorAll('ul,ol').forEach((list) => {
    if (!list.querySelector('li')) list.remove()
  })
  return root.innerHTML
}

/** The only CSS properties kept on an inline `style` — emphasis + color/size + the gradient-text trio.
 *  Longhands the browser expands shorthands into (e.g. text-decoration → text-decoration-line) are
 *  included so authored shorthands survive the parse. */
const INLINE_ALLOWED_STYLE_PROPS = new Set([
  'color',
  'font-weight',
  'font-style',
  'font-size',
  'text-decoration',
  'text-decoration-line',
  'text-decoration-style',
  'text-decoration-color',
  // Sub/superscript when a browser emits a styled span instead of <sub>/<sup>.
  'vertical-align',
  // Gradient text: a clipped background painted through the glyphs.
  'background',
  'background-image',
  'background-color',
  'background-clip',
  '-webkit-background-clip',
  '-webkit-text-fill-color',
])

/** DOMPurify hook (scoped per call): drop any style property outside the inline allowlist. */
function pruneInlineStyle(node: Element): void {
  const el = node as HTMLElement
  if (!el.style || el.getAttribute('style') == null) return
  const keep: string[] = []
  for (let i = 0; i < el.style.length; i++) {
    const prop = el.style[i]
    if (INLINE_ALLOWED_STYLE_PROPS.has(prop)) keep.push(`${prop}:${el.style.getPropertyValue(prop)}`)
  }
  if (keep.length) el.setAttribute('style', keep.join(';'))
  else el.removeAttribute('style')
}

/**
 * Sanitize a text element's inline rich-text HTML (see {@link sanitizeSlideHtml} for the slide-level
 * variant). Allows only inline emphasis tags + a pruned `style`; strips everything else. Used both when
 * BAKING a generated text node (freeze its runs) and when RENDERING / reading back the editable element,
 * so the same allowlist guards every path. The style-pruning hook is added and removed around this one
 * call so it never affects `sanitizeSlideHtml` (JS is single-threaded; this is synchronous).
 */
export function sanitizeInlineHtml(html: string): string {
  if (typeof window === 'undefined') return ''
  DOMPurify.addHook('afterSanitizeAttributes', pruneInlineStyle)
  let clean: string
  try {
    clean = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: INLINE_ALLOWED_TAGS,
      ALLOWED_ATTR: ['style'],
    })
  } finally {
    // Remove by reference (not a bare pop) so cleanup stays correct even if another hook is ever added.
    DOMPurify.removeHook('afterSanitizeAttributes', pruneInlineStyle)
  }
  return normalizeInlineLists(clean)
}
